import { useState } from 'react';
import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { css } from '@emotion/react';

import { ILazyDropdownOption } from 'components/lazyDropdown/LazyDropdown';
import { SridDropdown } from 'features/spatial/components/SridDropdown';
import { AmplitudeDomain } from 'features/seismic-3d/models/enums/AmplitudeDomain';
import { EVolumeType } from 'features/index/model/EVolumeType';
import { IQueueParamProps } from '../../../models/interfaces/IQueueParamProps';

const containerStyle = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    padding: 24,
    gap: 16,
    overflow: 'auto',
    '& .param-group': {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    '& .param-group-header': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    '& .param-group-title': {
        fontWeight: 700,
        color: '#616161',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    '& .param-fields-row': {
        display: 'flex',
        gap: 16,
        '& > *': {
            flex: 1,
        },
    },
    '& .param-group-split': {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    '& .param-group-split-main': {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
});

enum ScalarMode {
    Byte = 'byte',
    XY = 'xy',
}

const SCALAR_OPTIONS: { label: string; value: string }[] = [
    { label: '1', value: '1' },
    { label: '10', value: '0.1' },
    { label: '100', value: '0.01' },
    { label: '1000', value: '0.001' },
];

const ANP_DEFAULTS = {
    CoordinateX: '181',
    CoordinateY: '185',
    Inline: '221',
    XLine: '225',
    Trace: '25',
    ShotPoint: '17',
    Cdp: '21',
};

interface IQueueUploadSeismicParamProps extends IQueueParamProps {
    volumeType: EVolumeType;
}

export function QueueUploadSeismicParam({ volumeType, onParamsChange, initialValues }: IQueueUploadSeismicParamProps) {
    const iv = initialValues ?? {};
    const initialScalarMode = iv.ScalarX || iv.ScalarY ? ScalarMode.XY : ScalarMode.Byte;
    const initialSrid = iv.Srid ? { value: Number(iv.Srid), label: iv.Srid } as ILazyDropdownOption<number> : null;

    const [srid, setSrid] = useState<ILazyDropdownOption<number> | null>(initialSrid);
    const [parallelProcesses, setParallelProcesses] = useState(iv.ParallelProcesses ?? '20');
    const [coordinateX, setCoordinateX] = useState(iv.CoordinateX ?? '');
    const [coordinateY, setCoordinateY] = useState(iv.CoordinateY ?? '');
    const [inline, setInline] = useState(iv.Inline ?? '');
    const [xline, setXline] = useState(iv.XLine ?? '');
    const [scalarMode, setScalarMode] = useState<ScalarMode>(initialScalarMode);
    const [byteScalar, setByteScalar] = useState(iv.ByteScalar ?? '');
    const [scalarX, setScalarX] = useState(iv.ScalarX ?? '1');
    const [scalarY, setScalarY] = useState(iv.ScalarY ?? '1');
    const [trace, setTrace] = useState(iv.Trace ?? '');
    const [cdp, setCdp] = useState(iv.Cdp ?? '');
    const [shotPoint, setShotPoint] = useState(iv.ShotPoint ?? '');
    const [domain, setDomain] = useState<AmplitudeDomain>(iv.Domain ? Number(iv.Domain) as AmplitudeDomain : AmplitudeDomain.Depth);
    const [autopublish, setAutopublish] = useState(iv.AutoPublish?.toLowerCase() === 'true');

    function buildParams(overrides: Record<string, string>, mode = scalarMode): Record<string, string> {
        return {
            Srid: String(srid?.value ?? ''),
            VolumeType: String(volumeType),
            ParallelProcesses: parallelProcesses,
            CoordinateX: coordinateX,
            CoordinateY: coordinateY,
            Inline: inline,
            XLine: xline,
            ByteScalar: mode === ScalarMode.Byte ? byteScalar : '',
            ScalarX: mode === ScalarMode.XY ? scalarX : '',
            ScalarY: mode === ScalarMode.XY ? scalarY : '',
            Trace: trace,
            Cdp: cdp,
            ShotPoint: shotPoint,
            Domain: String(domain),
            AutoPublish: String(autopublish),
            ...overrides,
        };
    }

    function handleSridChange(value: ILazyDropdownOption<number> | null) {
        setSrid(value);
        onParamsChange?.(buildParams({ Srid: String(value?.value ?? '') }));
    }

    function handleChange(setter: (v: string) => void, key: string) {
        return (e: React.ChangeEvent<HTMLInputElement>) => {
            setter(e.target.value);
            onParamsChange?.(buildParams({ [key]: e.target.value }));
        };
    }

    function handleDomainChange(event: SelectChangeEvent<AmplitudeDomain>) {
        const value = event.target.value as AmplitudeDomain;
        setDomain(value);
        onParamsChange?.(buildParams({ Domain: String(value) }));
    }

    function handleAutopublishChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.checked;
        setAutopublish(value);
        onParamsChange?.(buildParams({ AutoPublish: String(value) }));
    }

    function handleScalarXChange(event: SelectChangeEvent<string>) {
        const value = event.target.value;
        setScalarX(value);
        onParamsChange?.(buildParams({ ScalarX: value }));
    }

    function handleScalarYChange(event: SelectChangeEvent<string>) {
        const value = event.target.value;
        setScalarY(value);
        onParamsChange?.(buildParams({ ScalarY: value }));
    }

    function handleScalarModeChange(_: React.MouseEvent, value: ScalarMode | null) {
        if (!value) {
            return;
        }
        setScalarMode(value);
        setByteScalar('');
        setScalarX('1');
        setScalarY('1');
        onParamsChange?.(buildParams({ ByteScalar: '', ScalarX: value === ScalarMode.XY ? '1' : '', ScalarY: value === ScalarMode.XY ? '1' : '' }, value));
    }

    function handleFillAnpDefaults() {
        setCoordinateX(ANP_DEFAULTS.CoordinateX);
        setCoordinateY(ANP_DEFAULTS.CoordinateY);
        setInline(ANP_DEFAULTS.Inline);
        setXline(ANP_DEFAULTS.XLine);
        setTrace(ANP_DEFAULTS.Trace);
        setShotPoint(ANP_DEFAULTS.ShotPoint);
        setCdp(ANP_DEFAULTS.Cdp);
        onParamsChange?.(buildParams(ANP_DEFAULTS));
    }

    return (
        <Box css={containerStyle}>
            <div className='param-fields-row'>
                <Box sx={{ flex: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Domain</InputLabel>
                        <Select<AmplitudeDomain>
                            value={domain}
                            label='Domain'
                            onChange={handleDomainChange}
                            MenuProps={{ style: { zIndex: 1000000 } }}
                        >
                            <MenuItem value={AmplitudeDomain.Depth}>Depth</MenuItem>
                            <MenuItem value={AmplitudeDomain.Time}>Time</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ flex: 3 }}>
                    <SridDropdown value={srid} onChange={handleSridChange} />
                </Box>
            </div>

            <div className='param-group'>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='caption' className='param-group-title'>Byte</Typography>
                    <Button variant='outlined' size='small' onClick={handleFillAnpDefaults} sx={{ textTransform: 'none' }}>
                        ANP
                    </Button>
                </Box>
                <div className='param-fields-row'>
                    <TextField label='X' type='number' value={coordinateX} onChange={handleChange(setCoordinateX, 'CoordinateX')} fullWidth />
                    <TextField label='Y' type='number' value={coordinateY} onChange={handleChange(setCoordinateY, 'CoordinateY')} fullWidth />
                    <TextField label='Inline' type='number' value={inline} onChange={handleChange(setInline, 'Inline')} fullWidth />
                    <TextField label='Xline' type='number' value={xline} onChange={handleChange(setXline, 'XLine')} fullWidth />
                </div>
                <div className='param-fields-row'>
                    <TextField label='Trace' type='number' value={trace} onChange={handleChange(setTrace, 'Trace')} fullWidth />
                    <TextField label='CDP' type='number' value={cdp} onChange={handleChange(setCdp, 'Cdp')} fullWidth />
                    <TextField label='Shot Point' type='number' value={shotPoint} onChange={handleChange(setShotPoint, 'ShotPoint')} fullWidth />
                </div>
            </div>

            <div className='param-group-split'>
                <div className='param-group-split-main'>
                    <div className='param-group-header'>
                        <Typography variant='caption' className='param-group-title'>Scalar</Typography>
                        <ToggleButtonGroup
                            value={scalarMode}
                            exclusive
                            onChange={handleScalarModeChange}
                            size='small'
                        >
                            <ToggleButton value={ScalarMode.Byte}>Byte</ToggleButton>
                            <ToggleButton value={ScalarMode.XY}>X / Y</ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                    {scalarMode === ScalarMode.Byte && (
                        <TextField
                            label='Byte Scalar'
                            type='number'
                            value={byteScalar}
                            onChange={handleChange(setByteScalar, 'ByteScalar')}
                            fullWidth
                        />
                    )}
                    {scalarMode === ScalarMode.XY && (
                        <div className='param-fields-row'>
                            <FormControl fullWidth>
                                <InputLabel>X</InputLabel>
                                <Select<string>
                                    value={scalarX}
                                    label='X'
                                    onChange={handleScalarXChange}
                                    MenuProps={{ style: { zIndex: 1000000 } }}
                                >
                                    {SCALAR_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Y</InputLabel>
                                <Select<string>
                                    value={scalarY}
                                    label='Y'
                                    onChange={handleScalarYChange}
                                    MenuProps={{ style: { zIndex: 1000000 } }}
                                >
                                    {SCALAR_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    )}
                </div>
            </div>

            <FormControlLabel
                control={<Checkbox checked={autopublish} onChange={handleAutopublishChange} />}
                label='Autopublish'
            />
        </Box>
    );
}
